// src/components/pages/Cart.jsx
import { Link, useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";
import axios from "axios";
import Faqs from "../sections/Faqs";
import { FaDown, FaUp, Trash } from "../../assets/icons/icons";
import { useTranslation } from "react-i18next";
import UseScrollAnimation from "../../hooks/UseScrollAnimation";

/* ===== Helpers ===== */
const fetchCoupons = async () => {
  const response = await fetch("https://laravel-api.aj-mart.com/api/coupon");
  const data = await response.json();
  return data.coupon;
};

const sanitizeCssColor = (val) => {
  if (!val || typeof val !== "string") return "";
  const s = val.trim();
  if (/^(rgb|hsl)a?\(/i.test(s)) return s;
  if (/^[a-z]+$/i.test(s)) return s;
  const hex = s.replace(/^#/, "");
  if (/^[0-9a-f]{3}$/i.test(hex) || /^[0-9a-f]{6}$/i.test(hex)) return `#${hex}`;
  return "";
};

const isHexString = (s) =>
  typeof s === "string" && /^#?[0-9a-f]{3}([0-9a-f]{3})?$/i.test(s.trim());

/* ======= Color name resolution (show name only) ======= */
const toArrayObjects = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val.filter(Boolean);
  if (typeof val === "object") return Object.values(val);
  if (typeof val === "string") {
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed)) return parsed.filter(Boolean);
      if (parsed && typeof parsed === "object") return Object.values(parsed);
    } catch {}
  }
  return [];
};

const getColorValue = (item) =>
  item?.selected_color ??
  item?.color_hex ??
  item?.product_color_hex ??
  item?.variation_color_hex ??
  item?.accessories_color_hex ??
  item?.item_data?.selected_color ??
  null;

const directColorName = (item) => {
  let n =
    item?.selected_color_name ??
    item?.color_name ??
    item?.variation_color_name ??
    item?.accessories_color_name ??
    item?.item_data?.selected_color_name ??
    null;
  if (typeof n === "string" && n.includes(",")) n = n.split(",")[0].trim();
  return n || null;
};

const colorNameFromMap = (item) => {
  const mapArr = [
    ...toArrayObjects(item?.product_colors_with_hex),
    ...toArrayObjects(item?.item_data?.product_colors_with_hex),
  ];
  if (!mapArr.length) return null;
  const val = sanitizeCssColor(getColorValue(item) || "");
  if (!val) return null;
  const needle = val.replace(/^#/, "").toLowerCase();
  for (const c of mapArr) {
    const hex = sanitizeCssColor(c?.hex || c?.color_hex || "").replace(/^#/, "").toLowerCase();
    if (hex && hex === needle) {
      const name = (c?.name || c?.label || c?.title || c?.color_name || "").trim();
      if (name) return name;
    }
  }
  return null;
};

const fallbackColorName = (item) => {
  // 1) لو selected_color كلمة (مش HEX) نعتبرها اسم
  const sel = item?.selected_color ?? item?.item_data?.selected_color ?? null;
  if (sel && !isHexString(sel)) return String(sel).split(",")[0].trim();

  // 2) product_color textual
  const pc =
    (typeof item?.product_color === "string" ? item.product_color : null) ??
    (typeof item?.item_data?.product_color === "string" ? item.item_data.product_color : null) ??
    null;
  if (pc && !isHexString(pc)) {
    return pc.includes(",") ? pc.split(",")[0].trim() : pc.trim();
  }

  // 3) accessories_color أول قيمة لو نصية/CSV/JSON
  const acc = (() => {
    const v = item?.accessories_color;
    if (!v) return null;
    if (Array.isArray(v)) return v[0] ?? null;
    if (typeof v === "string") {
      try {
        const arr = JSON.parse(v);
        if (Array.isArray(arr)) return arr[0] ?? null;
      } catch {}
      if (v.includes(",")) return v.split(",")[0].trim();
      return v;
    }
    return null;
  })();
  if (acc && !isHexString(acc)) return acc;

  return null;
};

const getColorName = (item) => {
  const direct = directColorName(item);
  if (direct) return direct;
  const fromMap = colorNameFromMap(item);
  if (fromMap) return fromMap;
  const fb = fallbackColorName(item);
  if (fb) return fb;
  return null;
};

const getCapacity = (item) =>
  item?.selected_capacity ??
  item?.capacity ??
  item?.product_capacity_selected ??
  item?.variation_capacity ??
  null;

/* NEW: حوّل أي قيمة لمصفوفة (Array / JSON / CSV) */
const toArray = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val.filter(Boolean);
  if (typeof val === "string") {
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed)) return parsed.filter(Boolean);
    } catch {}
    return val.split(",").map((s) => s.trim()).filter(Boolean);
  }
  return [];
};

/* NEW: استخرج الموديلات المختارة من عناصر الكارت */
const getModels = (item) =>
  toArray(
    item?.selected_models ??
      item?.accessories_selected_models ??
      item?.models ??
      item?.selected_model ??
      item?.item_data?.selected_models ??
      null
  );

/* Resolve coupon object by code safely */
const resolveCouponByCode = (code, list) => {
  if (!code) return null;
  const today = new Date().toISOString().split("T")[0];
  return (list || []).find(
    (c) =>
      c?.coupon_code === code &&
      today >= c?.coupon_start_date &&
      today <= c?.coupon_end_date
  );
};

axios
  .get("https://laravel-api.aj-mart.com/csrf-token")
  .then((response) => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = response.data.csrf_token;
  })
  .catch(() => {});
axios.defaults.withCredentials = true;

export default function Cart() {
  const { t } = useTranslation();
  UseScrollAnimation();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [couponCode, setCouponCode] = useState("");
  const [couponId, setCouponId] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [error, setError] = useState("");
  const [coupons, setCoupons] = useState([]);
  const [cartId, setCartId] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupType, setPopupType] = useState("success");

  // اسمع حدث التفريغ القادم من التشيك أوت
  useEffect(() => {
    const onCleared = () => {
      setProducts([]);
      localStorage.removeItem("aj_cart");
      localStorage.removeItem("aj_checkout_snapshot");
    };
    window.addEventListener("cart:cleared", onCleared);
    return () => window.removeEventListener("cart:cleared", onCleared);
  }, []);

  useEffect(() => {
    const getCoupons = async () => {
      const fetchedCoupons = await fetchCoupons();
      setCoupons(fetchedCoupons || []);
    };
    getCoupons();
  }, []);

  useEffect(() => {
    const fetchCart = async () => {
      try {
        const res = await axios.get("https://laravel-api.aj-mart.com/cart", {
          withCredentials: true,
        });

        // اجلب العناصر
        const cartItems =
          res.data.cart && Object.keys(res.data.cart).length > 0
            ? Object.keys(res.data.cart).map((key) => ({
                ...res.data.cart[key],
                cart_key: key,
              }))
            : [];
        setProducts(cartItems);
        localStorage.setItem("aj_cart", JSON.stringify(cartItems));

        // cartId
        let apiCartId = res.data?.cart_id || res.data?.id || null;
        let finalId = apiCartId || localStorage.getItem("aj_cart_id") || null;

        if (!finalId) {
          finalId = `cart_${Math.random().toString(36).slice(2, 10)}_${Date.now()}`;
          localStorage.setItem("aj_cart_id", finalId);
        }
        setCartId(finalId);
      } catch (error) {
        setProducts([]);
        const existingId = localStorage.getItem("aj_cart_id");
        if (existingId) setCartId(existingId);
      }
    };
    fetchCart();
  }, []);

  const updateQuantity = async (cart_key, newQuantity) => {
    try {
      const res = await axios.post(
        "https://laravel-api.aj-mart.com/cart/update",
        { cart_key, quantity: newQuantity },
        { withCredentials: true }
      );
      const updatedCart = res.data.cart || {};
      const cartItems = Object.keys(updatedCart).map((key) => ({
        ...updatedCart[key],
        cart_key: key,
      }));
      setProducts(cartItems);
      localStorage.setItem("aj_cart", JSON.stringify(cartItems));
    } catch {}
  };

  const incrementQuantity = (index) => {
    const product = products[index];
    updateQuantity(product.cart_key, product.quantity + 1);
  };
  const decrementQuantity = (index) => {
    const product = products[index];
    if (product.quantity > 1) updateQuantity(product.cart_key, product.quantity - 1);
  };

  const handleDelete = async (cart_key) => {
    try {
      const encodedCartKey = encodeURIComponent(cart_key);
      const res = await axios.delete(`https://laravel-api.aj-mart.com/cart/${encodedCartKey}`, {
        withCredentials: true,
      });
      const updatedCart = res.data.cart || {};
      const cartItems = Object.keys(updatedCart).map((key) => ({
        ...updatedCart[key],
        cart_key: key,
      }));
      setProducts(cartItems);
      localStorage.setItem("aj_cart", JSON.stringify(cartItems));
      setPopupMessage(t("Item removed from cart successfully"));
      setPopupType("success");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 2500);
    } catch (error) {
      setPopupMessage(t("حدث خطأ أثناء حذف العنصر من السلة"));
      setPopupType("error");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);
    }
  };

  const validateCoupon = () => {
    const validCoupon = resolveCouponByCode(couponCode, coupons);
    if (validCoupon) {
      setDiscount(Number(validCoupon.coupon_discount) || 0);
      setCouponId(validCoupon.id ?? validCoupon.coupon_id ?? null);
      setError("");
      setPopupMessage(t("Coupon applied successfully"));
      setPopupType("success");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 2500);
    } else {
      setDiscount(0);
      setCouponId(null);
      setError(t("Invalid coupon code or expired"));
      setPopupMessage(t("Invalid coupon code or expired"));
      setPopupType("error");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 2500);
    }
  };

  /* ===== الحسابات ===== */
  const subtotal = products.reduce((total, product) => {
    const priceKey = product.product_id ? "product_price" : "accessories_price";
    return total + product.quantity * parseFloat(product[priceKey]);
  }, 0);
  const delivery = 1;
  const discountAmount = subtotal * ((Number(discount) || 0) / 100);
  const total = (subtotal - discountAmount + delivery).toFixed(2);

  const handleProceedToCheckout = () => {
    let finalCouponId = couponId;
    if (!finalCouponId && couponCode) {
      const c = resolveCouponByCode(couponCode, coupons);
      if (c) finalCouponId = c.id ?? c.coupon_id ?? null;
    }

    const snapshot = {
      cartId,
      cartItems: products,
      subtotal,
      delivery,
      discountPercent: discount,
      discountAmount,
      total,
      couponCode,
      couponId: finalCouponId,
    };

    localStorage.setItem("aj_checkout_snapshot", JSON.stringify(snapshot));
    navigate("/checkout", { state: snapshot });
  };

  return (
    <>
      {/* Popup */}
      {showPopup && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50">
          <div className="bg-white rounded-lg shadow-2xl p-6 w-[350px] flex items-center gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${popupType === "success" ? "bg-green-500" : "bg-red-500"} text-white`}>
              {popupType === "success" ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/></svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></svg>
              )}
            </div>
            <div className="flex-1">
              <p className={`text-sm font-medium ${popupType === "success" ? "text-green-700" : "text-red-700"}`}>{popupMessage}</p>
            </div>
            <button onClick={() => setShowPopup(false)} className="text-gray-500 hover:text-gray-700 focus:outline-none">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
          </div>
        </div>
      )}

      {/* Hero */}
      <section className="landingPages">
        <div className="container h-full flex flex-col gap-[10px] justify-center">
          <div className="text-[35px] font-bold text-white">{t("Cart")}</div>
          <div className="flex items-center gap-[3px] text-white">
            <Link to="/">{t("Home")}</Link>
            <span>-</span>
            <div>{t("Cart")}</div>
          </div>
        </div>
      </section>

      {/* Items */}
      <section className="relative py-[40px]">
        <div className="container flex flex-col gap-3">
          {/* Header (desktop) */}
          <div className="hidden md:grid grid-cols-12 font-bold capitalize border border-[#eee] p-4 rounded-[8px] bg-white">
            <div className="col-span-6">{t("Product")}</div>
            <div className="col-span-2 text-center">{t("Quantity")}</div>
            <div className="col-span-2 text-center">{t("Subtotal")}</div>
          </div>

          {products.length === 0 ? (
            <div className="text-center py-10">{t("Your cart is empty")}</div>
          ) : (
            products.map((product, index) => {
              const priceKey = product.product_id ? "product_price" : "accessories_price";
              const nameKey = product.product_id ? "product_name_E" : "accessories_name_E";
              const imageKey = product.product_id ? "product_main_img" : "accessories_main_img";

              const colorName = getColorName(product); // ← اسم فقط
              const capacity = getCapacity(product);
              const models = getModels(product); // array of names

              const linePrice = parseFloat(product[priceKey]) || 0;
              const lineSubtotal = (product.quantity * linePrice).toFixed(2);

              return (
                <div key={product.cart_key} className="bg-white border border-[#eee] rounded-[8px] p-4">
                  {/* Mobile */}
                  <div className="md:hidden flex gap-3">
                    <img
                      src={`https://laravel-api.aj-mart.com/storage/app/public/${product[imageKey]}`}
                      alt={product[nameKey]}
                      className="w-20 h-20 object-contain flex-shrink-0"
                    />
                    <div className="flex-1 flex flex-col gap-1">
                      <div className="font-semibold text-sm">{product[nameKey]}</div>

                      {colorName && (
                        <div className="text-xs text-slate-600">
                          <span className="font-semibold">{t("Color")}:</span>{" "}
                          <span className="inline-block rounded-full border px-2 py-[2px] bg-slate-50">{colorName}</span>
                        </div>
                      )}

                      {capacity && (
                        <div className="text-xs text-slate-600">
                          <span className="font-semibold">{t("Capacity")}:</span>{" "}
                          <span className="inline-block rounded-full border px-2 py-[2px] bg-slate-50">{capacity}</span>
                        </div>
                      )}

                      {models.length > 0 && (
                        <div className="text-xs text-slate-600 flex items-start gap-2 flex-wrap">
                          <span className="font-semibold">{t("Models")}:</span>
                          <div className="flex flex-wrap gap-1">
                            {models.map((m, i) => (
                              <span key={i} className="inline-block rounded-full border px-2 py-[2px] bg-slate-50">
                                {m}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="mt-2 grid grid-cols-3 gap-2 items-center">
                        <div className="flex items-center justify-center gap-2 border border-[#eee] px-3 py-1.5 rounded-[6px]">
                          <span className="text-sm">{product.quantity}</span>
                          <div className="flex flex-col">
                            <button onClick={() => incrementQuantity(index)} className="cursor-pointer"><FaUp /></button>
                            <button onClick={() => decrementQuantity(index)} className="cursor-pointer"><FaDown /></button>
                          </div>
                        </div>

                        <div className="text-center text-[13px]">
                          <div className="text-slate-500">{t("Subtotal")}</div>
                          <div className="font-semibold">{lineSubtotal} {t("JOD")}</div>
                        </div>
                      </div>
                    </div>

                    <button onClick={() => handleDelete(product.cart_key)} className="self-start text-red-500 hover:opacity-80" aria-label={t("Remove")} title={t("Remove")}>
                      <Trash />
                    </button>
                  </div>

                  {/* Desktop */}
                  <div className="hidden md:grid grid-cols-12 items-center gap-3">
                    <div className="col-span-6 flex items-center gap-3">
                      <img
                        src={`https://laravel-api.aj-mart.com/storage/app/public/${product[imageKey]}`}
                        alt={product[nameKey]}
                        className="w-20 h-20 object-contain flex-shrink-0"
                      />
                      <div className="flex flex-col">
                        <div className="font-semibold">{product[nameKey]}</div>
                        <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-slate-600">
                          {colorName && (
                            <span className="inline-flex items-center gap-1">
                              <span className="font-medium">{t("Color")}:</span>
                              <span className="inline-block rounded-full border px-2 py-[2px] bg-slate-50">{colorName}</span>
                            </span>
                          )}
                          {capacity && (
                            <span className="inline-flex items-center gap-1">
                              <span className="font-medium">{t("Capacity")}:</span>
                              <span className="inline-block rounded-full border px-2 py-[2px] bg-slate-50">{capacity}</span>
                            </span>
                          )}
                          {models.length > 0 && (
                            <span className="inline-flex items-center gap-1">
                              <span className="font-medium">{t("Models")}:</span>
                              <span className="flex flex-wrap gap-1">
                                {models.map((m, i) => (
                                  <span key={i} className="inline-block rounded-full border px-2 py-[2px] bg-slate-50">
                                    {m}
                                  </span>
                                ))}
                              </span>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-span-2">
                      <div className="flex items-center justify-center gap-2 border border-[#eee] px-5 py-1.5 rounded-[6px] w-fit mx-auto">
                        <span className="min-w-[16px] text-center">{product.quantity}</span>
                        <div className="flex flex-col">
                          <button onClick={() => incrementQuantity(index)} className="cursor-pointer"><FaUp /></button>
                          <button onClick={() => decrementQuantity(index)} className="cursor-pointer"><FaDown /></button>
                        </div>
                      </div>
                    </div>

                    <div className="col-span-2 text-center">
                      {(product.quantity * linePrice).toFixed(2)} {t("JOD")}
                    </div>

                    <div className="col-span-2 md:col-span-0 flex md:block justify-end">
                      <button onClick={() => handleDelete(product.cart_key)} className="text-red-500 hover:opacity-80" aria-label={t("Remove")} title={t("Remove")}>
                        <Trash />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </section>

      {/* Summary */}
      <section className="relative py-[40px]">
        <div className="container grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="border border-gray-200 p-5 rounded-md bg-white shadow-sm">
            <div className="text-[20px] font-bold mb-4">{t("Cart Total")}</div>

            <div className="flex justify-between py-1">
              <div>{t("Subtotal")}:</div>
              <div>{subtotal.toFixed(2)} {t("JOD")}</div>
            </div>

            <div className="flex justify-between py-1">
              <div>{t("Delivery")}:</div>
              <div>{delivery.toFixed(2)} {t("JOD")}</div>
            </div>

            {discount > 0 && (
              <div className="flex justify-between py-1 text-red-600">
                <div>{t("Discount")} ({Number(discount)}%)</div>
                <div>-{discountAmount.toFixed(2)} {t("JOD")}</div>
              </div>
            )}

            <div className="flex justify-between py-2 border-t mt-3 font-semibold">
              <div>{t("Total")}:</div>
              <div>{total} {t("JOD")}</div>
            </div>

            <button onClick={handleProceedToCheckout} className="btn w-full mt-3">
              {t("Proceed to checkout")}
            </button>

            <div className="flex gap-2 mt-4">
              <input
                className="border border-gray-300 w-[60%] p-2 rounded"
                type="text"
                placeholder={t("Coupon Code")}
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
              />
              <button onClick={validateCoupon} className="btn">
                {t("Apply Coupon")}
              </button>
            </div>

            {error && <div className="text-red-500 mt-2">{error}</div>}
          </div>

          <div>
            <div className="text-[25px] font-bold mb-3">{t("Delivery & Return")}</div>
            <Faqs />
          </div>
        </div>
      </section>
    </>
  );
}
